import { useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { createContext, PropsWithChildren, useCallback, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { SellerUserClient, UserSetting } from 'src/WebApiController';

type UserSettingsMap = {
  [key in keyof typeof UserSetting]?: string;
};

type ISellerUserAllSettingsContext = {
  queryResult?: UserSettingsMap | null;
  contextSettingIds?: UserSetting[];
  contextQueryKey?: (string | boolean | UserSetting[] | undefined)[];
  isLoading?: boolean;
  refresh?: () => void;
} | null;

export const SellerUserAllSettingsContext =
  createContext<ISellerUserAllSettingsContext>(null);

type SellerUserAllSettingsProviderProps = PropsWithChildren<{}>;

export const SellerUserAllSettingsProvider = ({
  children,
}: SellerUserAllSettingsProviderProps) => {
  const { trackError } = useClientLogContext();

  const { activeAccountWebClientConfig } = useAppContext();
  const sellerUserClient = useMemo(
    () => new SellerUserClient(activeAccountWebClientConfig),
    [activeAccountWebClientConfig]
  );

  const queryKey = [
    'allUserSettings',
    activeAccountWebClientConfig.activeAccountId,
  ];

  const shouldQuery = activeAccountWebClientConfig?.activeAccountId != null;

  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }

      return await sellerUserClient.getAllSellerUserSettings(
        // These have special handling in the UI so we want to make sure we load them
        // see useUserSetting.ts
        [
          UserSetting.QuickFiltersCustomInventory,
          UserSetting.QuickFiltersCustomSales,
          UserSetting.QuickFiltersCustomPurchases,
          UserSetting.QuickFiltersCustomOnSaleEvent,
          UserSetting.QuickFiltersCustomMarketplacePayments,
        ],
        true
      );
    },

    meta: {
      persist: false,
      onError: (error: ErrorTypes) => {
        trackError('SellerUserClient.getAllSellerUserSettings', error, {
          trackErrorData: {},
        });
      },
    },
    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queryClient = useQueryClient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refresh = useCallback(
    debounce(() => {
      queryClient.invalidateQueries({
        queryKey: queryKey,
      });
    }, 500), // 500ms debounce so bunch of saves to user settings don't trigger a ton of invalidations
    [queryClient, queryKey]
  );

  const isLoading = useMemo(
    () =>
      !queryResult.isFetched ||
      queryResult.isRefetching ||
      queryResult.isPending,
    [queryResult.isFetched, queryResult.isPending, queryResult.isRefetching]
  );

  return (
    <SellerUserAllSettingsContext.Provider
      value={{
        queryResult: queryResult.data,
        isLoading,
        refresh,
        contextSettingIds: Object.keys(queryResult.data ?? {}).map(
          (key) => UserSetting[key as keyof typeof UserSetting]
        ),
        contextQueryKey: queryKey,
      }}
    >
      {children}
    </SellerUserAllSettingsContext.Provider>
  );
};
